import { useLazyQuery } from "@apollo/client"
import { UseQueryOptions } from "@oysterhr/core"
import { Company } from "@oysterhr/schema"
import { Toast, useToast } from "@oysterhr/ui"
import { useCallback, useState } from "react"

import { useTranslation } from "react-i18next"
import { PAYMENTS_COMPANY_GET_PAYMENT_METHODS } from "../../hooks/useNewCompanyPaymentMethods"

type Payload = {
  company: Company
}

type Args = {
  companyId: string
}

export const useMandatePolling = (options?: UseQueryOptions<Payload, Args>) => {
  const [isPolling, setIsPolling] = useState(false)
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage/SepaFlow")
  const toast = useToast()

  const [query, result] = useLazyQuery<Payload, Args>(PAYMENTS_COMPANY_GET_PAYMENT_METHODS, {
    ...options,
    fetchPolicy: "network-only",
    pollInterval: 5000,
    notifyOnNetworkStatusChange: true,
    onCompleted: data => {
      const signMandateURL = data.company?.customerBankAccount?.mandate?.signerRecipientViewUrl
      if (signMandateURL) {
        result.stopPolling()
        setIsPolling(false)
        // Open in the current tab
        window.location.href = signMandateURL
      }
    },
    onError: error => {
      result.stopPolling()
      setIsPolling(false)
      toast({
        render: (
          <Toast variation="error">
            <Toast.Content>{error.message}</Toast.Content>
          </Toast>
        ),
      })
    },
  })

  const startPolling = useCallback(
    (companyId: string, showToast: boolean = true) => {
      if (isPolling) return

      setIsPolling(true)
      showToast &&
        toast({
          render: (
            <Toast variation="info">
              <Toast.Title>{t("authorizationStep.toast.mandate.title")}</Toast.Title>
              <Toast.Content>{t("authorizationStep.toast.mandate.content")}</Toast.Content>
            </Toast>
          ),
        })

      query({ variables: { companyId } })
    },
    [query, isPolling, setIsPolling, t, toast],
  )

  return { startPolling, isPolling }
}
